import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@monksflow/monks-ui';
import { DownloadIcon } from 'lucide-react';
import {
  taxonomyDatasetDetailQueryOptions,
  taxonomyRecordsQueryOptions,
  useExportRecords,
  useMoveTaxonomyDatasetToCampaign,
  useRenameTaxonomyDataset,
} from '@/entities/taxonomy-dataset';
import { taxonomyCampaignsQueryOptions } from '@/entities/taxonomy-campaign';
import {
  getRuleBranches,
  TAXONOMY_QUERY_KEYS,
  taxonomyDetailQueryOptions,
  type TaxonomyField,
  type TaxonomyRule,
} from '@/entities/taxonomy';
import { usePermissions } from '@/entities/user';
import { OpenPageAgentButton, PageAgentDock } from '@/entities/agent';
import { PageTransition } from '@/shared/ui/animations';
import {
  ListPageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderEditableName,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { ROUTES } from '@/shared/config/routes';
import {
  gridColumns,
  isRowComplete,
  TaxonomyRecordGrid,
  useEffectiveFields,
} from '@/features/taxonomy-record-grid';
import { useResolvedListValues } from '@/features/taxonomy-list-binding';
import { useTaxonomyAgentHost } from '@/features/taxonomy-agents';
import { isFieldInName } from '@inference.engine/taxonomy/web';
import { ExportWarningDialog } from './components/ExportWarningDialog/ExportWarningDialog';

const NO_FIELDS: TaxonomyField[] = [];
const NO_RULES: TaxonomyRule[] = [];

/**
 * At rest the campaign picker reads as a crumb; hover, keyboard focus and an open menu give it the
 * same fill as the editable name beside it, and show the chevron.
 */
const QUIET_CRUMB_TRIGGER =
  'type-body-md-strong h-auto w-auto min-w-0 -mx-1.5 gap-1 rounded-sm border-transparent ' +
  'bg-transparent px-1.5 py-0.5 shadow-none hover:bg-ghost-hover focus-visible:bg-ghost-hover ' +
  'data-[state=open]:bg-ghost-hover [&>svg]:opacity-0 hover:[&>svg]:opacity-50 ' +
  'focus-visible:[&>svg]:opacity-50 data-[state=open]:[&>svg]:opacity-50';

/**
 * Stands in for `null` in the campaign picker.
 *
 * Radix `Select` reads an empty string as "nothing selected" and shows the
 * placeholder, so Ungrouped needs a value of its own to be selectable at all.
 */
// eslint-disable-next-line i18next/no-literal-string -- a Select value, never rendered
const UNGROUPED_VALUE = 'ungrouped';

export function TaxonomyGenerationDetail() {
  const { t } = useTranslation('knowledge');
  const { datasetId = '' } = useParams<{ datasetId: string }>();
  const { canManageTaxonomy } = usePermissions();
  const queryClient = useQueryClient();

  const datasetQuery = useQuery(taxonomyDatasetDetailQueryOptions(datasetId));
  const recordsQuery = useQuery(taxonomyRecordsQueryOptions(datasetId));

  const dataset = datasetQuery.data;

  // The schema supplies the columns, so the grid cannot render without it.
  const schemaQuery = useQuery({
    ...taxonomyDetailQueryOptions(dataset?.taxonomyId ?? ''),
    enabled: Boolean(dataset?.taxonomyId),
  });

  const pinnedVersion = useMemo(
    () => schemaQuery.data?.versions.find((v) => v.id === dataset?.taxonomyVersionId),
    [schemaQuery.data, dataset?.taxonomyVersionId],
  );
  /**
   * When the agent moves this taxonomy onto a newer version, the dataset is re-pinned at once and
   * the schema is refetched behind it. Until the refetch lands the new version is not in the
   * cached schema, and the page would flash its missing-version state; the last version shown
   * stands in meanwhile.
   */
  const lastVersion = useRef(pinnedVersion);
  if (pinnedVersion) lastVersion.current = pinnedVersion;
  const version = pinnedVersion ?? (schemaQuery.isFetching ? lastVersion.current : undefined);

  /**
   * Composed here rather than inside the grid: both are `features/` slices, and
   * one feature may not import another. The page is the lowest layer that may
   * see both.
   */
  const resolvedLists = useResolvedListValues(version?.fields ?? NO_FIELDS);

  const rename = useRenameTaxonomyDataset(datasetId);
  const moveCampaign = useMoveTaxonomyDatasetToCampaign(datasetId);
  const campaignsQuery = useQuery(taxonomyCampaignsQueryOptions());

  /**
   * The name the file arrives under, built here because nothing else can.
   *
   * The export is fetched as a blob, and a `blob:` URL carries no
   * `Content-Disposition` — the browser names the download from `a.download`
   * alone, so the server's own filename never reaches the user however carefully
   * it is composed. The page is where all three parts are already loaded.
   *
   * The version is the one this dataset is PINNED to, not the schema's head: a
   * republish must not relabel a file whose rows were assembled from the old shape.
   * The campaign leads, as in the server's own name, so a campaign's files sort together.
   * Until the campaigns load, the name has no campaign; the download reads the name that is
   * current when its response arrives.
   */
  const campaignName = dataset?.campaignId
    ? campaignsQuery.data?.find((campaign) => campaign.id === dataset.campaignId)?.name
    : undefined;
  const exportName =
    dataset && version && schemaQuery.data
      ? campaignName
        ? t('taxonomy.generationDetail.exportFileNameInCampaign', {
            campaign: campaignName,
            dataset: dataset.name,
            schema: schemaQuery.data.name,
            version: version.versionNumber,
          })
        : t('taxonomy.generationDetail.exportFileName', {
            dataset: dataset.name,
            schema: schemaQuery.data.name,
            version: version.versionNumber,
          })
      : (dataset?.name ?? '');
  const exportRecords = useExportRecords(datasetId, exportName);

  /**
   * The node the grid portals its Save button into.
   *
   * State rather than a ref: a ref's mutation does not re-render, so the grid
   * would read `null` on the pass where the slot first exists and never portal.
   */
  const [saveSlot, setSaveSlot] = useState<HTMLDivElement | null>(null);

  /**
   * The optimistic write and its rollback belong to the mutation, not here — a
   * per-call `onError` does not survive a second rename. See
   * `useRenameTaxonomyDataset`.
   */
  const commitName = useCallback(
    (next: string) => {
      if (!dataset || next === dataset.name) {
        return;
      }
      rename.mutate(next);
    },
    [dataset, rename],
  );

  const [dirty, setDirty] = useState(false);

  const agent = useTaxonomyAgentHost(datasetId);
  /**
   * The open dialog, holding EVERYTHING it was opened with.
   *
   * Latched, not derived - every field, including the row total. Reading any of
   * them live while the dialog is up lets it contradict itself, and both ways
   * this went wrong were reachable:
   *
   *   - reading `unsaved` live let the dialog EMPTY itself. When unsaved changes
   *     were the only caveat and the save landed behind the dialog, it stayed
   *     open (correctly) with nothing left in its list - promising to say what to
   *     know, then saying nothing, while still gating the download.
   *   - `total` shares one sentence with `incomplete` ("{{count}} of {{total}}
   *     rows"), so latching one and reading the other let the two halves
   *     disagree. Confirming a Delete closes its own dialog immediately, which
   *     frees Export, and the delete then invalidates the records query this page
   *     reads. Latched for that reason rather than a reproduced one: no test here
   *     manages to re-render an open dialog from that refetch, so this half is
   *     consistency, not a pinned fix.
   *
   * The sentences stay true after the fact - "the file contains what has been
   * saved" holds whether or not the save has landed - so a snapshot is honest as
   * well as stable. The file itself is always computed fresh at confirm time.
   */
  const [warning, setWarning] = useState<{
    incomplete: number;
    total: number;
    rulesEscapeFormula: boolean;
    nameFormulaWithheld: boolean;
    unsaved: boolean;
  } | null>(null);

  const records = useMemo(() => recordsQuery.data ?? [], [recordsQuery.data]);

  /**
   * Rule evaluation, owned here so the grid and this page share one cache.
   *
   * The grid used to own it, and the comment that stood here claimed a page could
   * not reach it. That was wrong: `useRecordDraft` returns the record OBJECT
   * ITSELF for an unedited row, so both hold the same `WeakMap` keys — and the
   * grid has already evaluated every row, not just the visible window, before
   * Export is ever pressed. Reading the cache instead of recomputing measured
   * ~30ms against ~310ms at the record cap.
   */
  const effectiveFor = useEffectiveFields(
    version?.fields ?? NO_FIELDS,
    version?.rules ?? NO_RULES,
    resolvedLists,
  );

  /**
   * Rows not fit to save, by the SAME predicate the grid's markers and its Save
   * gate use.
   *
   * It used to be a second, independently-written definition, and the two
   * disagreed on seven kinds of input. The one that mattered: this side ignored
   * `required` entirely, so in a wide schema it reported every row incomplete
   * while every marker in the grid was clear — nothing to fix and nothing to
   * reconcile, which teaches people to click straight through the warning and
   * lose the two that are real.
   *
   * Still deferred to dialog-open. It is cheap now, but `hasWarnings` gates the
   * dialog on it and unwinding that is a separate change.
   */
  const countIncomplete = useCallback(() => {
    if (!version) {
      return 0;
    }
    const columns = gridColumns(version.fields);
    return records.filter((record) => !isRowComplete(columns, effectiveFor(record), record.cells))
      .length;
  }, [version, records, effectiveFor]);

  /**
   * Only two rule kinds escape a spreadsheet formula.
   *
   * `limit_options` narrows a vocabulary per record and `show_field` drops a field
   * from one, neither of which a single formula per column can express.
   * `require_field` and `set_default` change nothing the formula reads, so keying
   * this on `rules.length` alone would warn about schemas that are fine.
   *
   * Branches are read through `getRuleBranches` because rules saved before
   * `branches` existed carry the flat shape, and reading `rule.branches` directly
   * misses them entirely.
   */
  const rulesEscapeFormula = useMemo(
    () =>
      (version?.rules ?? []).some((rule) =>
        getRuleBranches(rule).some(
          (branch) => branch.action === 'show_field' || branch.action === 'limit_options',
        ),
      ),
    [version?.rules],
  );

  /**
   * Whether the name column will carry a fixed string rather than a live
   * calculation.
   *
   * Exactly the condition the export applies: a `linked` field that is part of
   * the name cannot be put in the sheet at all - nothing resolves one - so the
   * formula is withheld rather than emitted with a token missing. Every other
   * in-name field is carried, hidden if the schema hides it. Derived from the
   * schema here rather than reported by the server, because this dialog is shown
   * before the export request is made.
   */
  const nameFormulaWithheld = useMemo(
    () => (version?.fields ?? []).some((f) => isFieldInName(f) && f.type === 'linked'),
    [version?.fields],
  );

  /**
   * Decided here, not in an effect afterwards.
   *
   * The dialog used to open unconditionally and an effect closed it again when
   * the warnings turned out to be empty. That effect re-ran on every change to
   * `hasWarnings`, not only on open - so a save landing while the dialog was up
   * flipped `dirty` false, and the dialog vanished mid-read and started the
   * download with no answer from the user. Deciding at the click makes that
   * sequence unrepresentable rather than guarded.
   */
  const startExport = useCallback(() => {
    const incomplete = countIncomplete();
    if (incomplete > 0 || rulesEscapeFormula || nameFormulaWithheld || dirty) {
      setWarning({
        incomplete,
        total: records.length,
        rulesEscapeFormula,
        nameFormulaWithheld,
        unsaved: dirty,
      });
      return;
    }
    exportRecords.mutate();
  }, [
    countIncomplete,
    records.length,
    rulesEscapeFormula,
    nameFormulaWithheld,
    dirty,
    exportRecords,
  ]);

  /**
   * Both counts need the schema and the records, and the schema loads a round trip AFTER
   * the dataset — so for a moment the button is live while `version` is undefined, and every
   * warning would compute as absent. "Nothing to say" and "cannot tell yet" would
   * be indistinguishable, and a 20 000-row dataset would download unwarned. Records that
   * failed to load are the same case.
   */
  const canJudgeExport = Boolean(version) && recordsQuery.data !== undefined;

  const loading = datasetQuery.isLoading || recordsQuery.isLoading || schemaQuery.isLoading;
  // Kept data outlives a failed refetch, so only a first load that failed hides the grid.
  const recordsFailed = recordsQuery.isError && !recordsQuery.data;
  // A 404 is a schema deleted under its datasets, which the missing-version state explains.
  const schemaFailed =
    schemaQuery.isError &&
    !schemaQuery.data &&
    parseProblemDetail(schemaQuery.error, '').status !== 404;

  if (datasetQuery.isError) {
    return (
      <PageTransition>
        <div className="p-md">
          <EmptyState
            variant="outline-dashed"
            title={t('taxonomy.generationDetail.notFoundTitle')}
            description={t('taxonomy.generationDetail.notFoundDescription')}
            role="alert"
          />
        </div>
      </PageTransition>
    );
  }

  return (
    <div className="page-layout gap-app-inset">
      <PageTransition variant="slideUp" delay={0.0}>
        <PageHeader>
          <PageHeaderIdentity>
            <PageHeaderBreadcrumb>
              <PageHeaderBreadcrumbLink href={ROUTES.TOOLS_TAXONOMY_GENERATION}>
                {t('taxonomy.generationList.title')}
              </PageHeaderBreadcrumbLink>
              {/*
                The one place a taxonomy can change campaigns. Without it a
                taxonomy filed under the wrong campaign at creation would be stuck
                there for good — the create modal is the only other place campaign
                is ever asked for.

                Held back until the campaigns have actually ARRIVED. Not
                `!isLoading`, which is also false on error, and not `isSuccess`,
                which turns false again when a background REFETCH fails even though
                the cached list is still perfectly good — this control is warm from
                the list page, so a single focus-refetch blip would make it vanish.
                What matters is having options to offer, so it asks for the data.

                Until there is any, a taxonomy that IS in a campaign has a value
                matching no option, and Radix renders the trigger blank (it shows a
                placeholder only for '' or undefined, and a uuid is neither) — which
                reads as "no campaign" for exactly the rows where that is wrong, and
                the next selection writes that misreading back.

                A plain span for read-only callers, never `PageHeaderBreadcrumbPage`:
                Tessra's page crumb is `aria-current="page"`, and this is not the page.
              */}
              {dataset &&
                campaignsQuery.data !== undefined &&
                (canManageTaxonomy ? (
                  <Select
                    key="campaign"
                    value={dataset.campaignId ?? UNGROUPED_VALUE}
                    onValueChange={(next) =>
                      moveCampaign.mutate(next === UNGROUPED_VALUE ? null : next)
                    }
                    disabled={moveCampaign.isPending}
                  >
                    <SelectTrigger
                      className={QUIET_CRUMB_TRIGGER}
                      aria-label={t('taxonomy.campaign.moveToCampaign')}
                      data-testid="taxonomy-detail-campaign"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNGROUPED_VALUE}>
                        {t('taxonomy.campaign.ungrouped')}
                      </SelectItem>
                      {campaignsQuery.data.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span
                    key="campaign"
                    className="truncate text-sm font-medium text-foreground"
                    data-testid="taxonomy-detail-campaign"
                  >
                    {campaignsQuery.data.find((c) => c.id === dataset.campaignId)?.name ??
                      t('taxonomy.campaign.ungrouped')}
                  </span>
                ))}
              {/*
                Read-only callers get the plain crumb: the editable one offers a
                rename on click that would refuse on commit anyway.
              */}
              {dataset &&
                (canManageTaxonomy ? (
                  <PageHeaderEditableName
                    key="name"
                    value={dataset.name}
                    onCommit={commitName}
                    label={t('taxonomy.generationDetail.renameTaxonomy')}
                  />
                ) : (
                  <PageHeaderBreadcrumbPage key="name">{dataset.name}</PageHeaderBreadcrumbPage>
                ))}
            </PageHeaderBreadcrumb>
            {version && (
              <span className="shrink-0 text-sm text-muted-foreground">
                {t('taxonomy.generationDetail.schemaSuffix', {
                  name: schemaQuery.data?.name ?? '',
                  version: version.versionNumber,
                })}
              </span>
            )}
          </PageHeaderIdentity>

          {/* `saveSlot` is where the grid portals its own Save button; the draft it acts on stays
              in the grid. */}
          <PageHeaderActions>
            {dataset && (
              <Button
                variant="outline"
                size="default"
                onClick={startExport}
                disabled={exportRecords.isPending || !canJudgeExport}
              >
                <DownloadIcon className="size-4" />
                {t('taxonomy.grid.export')}
              </Button>
            )}
            <div ref={setSaveSlot} className="contents" />
          </PageHeaderActions>
        </PageHeader>
      </PageTransition>

      <div className="flex min-h-0 flex-1">
        {canManageTaxonomy && <PageAgentDock panel={agent.panel} chat={agent.chat} />}
        <ListPageBody>
          <div className="flex min-h-0 flex-1 flex-col gap-md overflow-auto px-section py-section">
            {loading && <Skeleton className="h-96 w-full" />}

            {!loading && recordsFailed && (
              <EmptyState
                variant="outline-dashed"
                title={t('taxonomy.generationDetail.recordsErrorTitle')}
                description={t('taxonomy.generationDetail.loadErrorDescription')}
                role="alert"
              >
                <Button variant="outline" onClick={() => void recordsQuery.refetch()}>
                  {t('taxonomy.generationDetail.tryAgain')}
                </Button>
              </EmptyState>
            )}

            {!loading && schemaFailed && (
              <EmptyState
                variant="outline-dashed"
                title={t('taxonomy.generationDetail.schemaErrorTitle')}
                description={t('taxonomy.generationDetail.loadErrorDescription')}
                role="alert"
              >
                <Button variant="outline" onClick={() => void schemaQuery.refetch()}>
                  {t('taxonomy.generationDetail.tryAgain')}
                </Button>
              </EmptyState>
            )}

            {!loading && !recordsFailed && dataset && version && (
              <TaxonomyRecordGrid
                datasetId={dataset.id}
                fields={version.fields}
                settings={version.settings}
                outputs={version.outputs}
                rules={version.rules}
                records={records}
                readOnly={!canManageTaxonomy}
                resolvedLists={resolvedLists}
                saveSlot={saveSlot}
                effectiveFor={effectiveFor}
                onDirtyChange={setDirty}
                onDirtyIdsChange={agent.onDirtyIdsChange}
                highlightedIds={agent.highlightedIds}
                leading={
                  canManageTaxonomy && <OpenPageAgentButton panel={agent.panel} chat={agent.chat} />
                }
                fill
                codesIssued={schemaQuery.data?.trackingCodeCount ?? 0}
                onCodesSettled={() => {
                  // `trackingCodeCount` is on the taxonomy, and a mint moves it. Without
                  // this the spend confirmation kept quoting a pre-mint allowance for the
                  // whole `staleTime` — the one number that decides whether to spend.
                  void queryClient.invalidateQueries({
                    queryKey: TAXONOMY_QUERY_KEYS.detail(dataset.taxonomyId),
                  });
                }}
              />
            )}

            {!loading && !schemaFailed && dataset && !version && (
              <EmptyState
                variant="outline-dashed"
                title={t('taxonomy.generationDetail.missingVersionTitle')}
                description={t('taxonomy.generationDetail.missingVersionDescription')}
                role="alert"
              />
            )}
          </div>
        </ListPageBody>
      </div>

      <ExportWarningDialog
        open={warning !== null}
        onOpenChange={(next) => !next && setWarning(null)}
        incomplete={warning?.incomplete ?? 0}
        total={warning?.total ?? 0}
        rulesEscapeFormula={warning?.rulesEscapeFormula ?? false}
        nameFormulaWithheld={warning?.nameFormulaWithheld ?? false}
        unsaved={warning?.unsaved ?? false}
        onConfirm={() => {
          setWarning(null);
          exportRecords.mutate();
        }}
      />
    </div>
  );
}
